'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { BaseTranslations } from '../../../baseTranslations';
import { PeriodGift } from '../../../types/cmsTypes';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getUrgencyPhase } from '../../../utils/helpers/calculateRewardValue';
import { getGiftLabels } from '../../../utils/helpers/giftLabels';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { FeCarouselV2 } from '../../FeCarousel';
import { RewardThumbnail } from '../../RewardThumbnail';
import { RichText } from '../../RichText/index';
import { ProductDetailsSectionWrapper } from './ProductDetailsSectionWrapper';

export type ProductDetailsGiftGroupProps = {
  /** Which group this is: the choose-one carousel or the welcome-offer (bank / provider gift) group. Also its test hook. */
  group: 'choose-one' | 'welcome';
  title: string;
  /** Lead-in line under the heading; omitted where `hasOfferGroupLeadIn` is false for the vertical. */
  info?: string;
  gifts: PeriodGift[];
};

export const ProductDetailsGiftGroup: React.FC<
  ProductDetailsGiftGroupProps
> = ({ group, title, info, gifts }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);

  return (
    <div
      data-component="product-details-gift-group"
      data-testid={`product-details-gift-group-${group}`}
    >
      <ProductDetailsSectionWrapper>
        <div className="px-6 pt-6 pb-4 flex flex-col gap-6">
          <h3 className="alpha-subheading-2 text-text/accent m-0">{title}</h3>
          {info && (
            <div className="mhg-alpha-typography-body-2-relaxed text-text/secondary-lighter">
              <RichText>{info}</RichText>
            </div>
          )}

          <FeCarouselV2
            navigation={{ showOnDesktop: true }}
            pagination={{ showOnMobile: true }}
          >
            {gifts.map((gift) => {
              const giftValue = gift.gift_value;
              const labels = getGiftLabels(gift, staticText);
              return (
                <div key={gift.uid}>
                  <RewardThumbnail
                    data-gift-uid={gift.uid}
                    image={gift.desktop_image}
                    tooltip={gift.gift_title}
                    eyebrow={gift.offerTitle}
                    title={labels.title}
                    subtitle={labels.subtitle}
                    rewardThumbnailSizeClasses={classNames(
                      'h-32 w-[145px] md:h-36 md:w-41 xl:h-36 xl:w-[158px]',
                    )}
                    tagText={(() => {
                      const urgencyLabel = giftValue?.urgency_label;
                      if (urgencyLabel) return urgencyLabel;

                      return getUrgencyPhase(
                        context.lang,
                        gift.startDate,
                        gift.endDate,
                      )?.value;
                    })()}
                    urgentTagText={!!giftValue?.urgency_label}
                  />
                </div>
              );
            })}
          </FeCarouselV2>
        </div>
      </ProductDetailsSectionWrapper>
    </div>
  );
};
